/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { UserChosenIcon } from './UserChosenIcon';
import { classNames } from '../utils/UNSAFE_classNames';
import { MessageSeverity } from './TrainIcons.types';
import { useContext } from 'preact/hooks';
import { Context, TrainContextType } from './TrainContext';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { useActive } from '../hooks/UNSAFE_useActive';

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TrainVariantOptions, TrainStyles } from './themes/TrainStyles.css';
import { TrainRedwoodTheme } from './themes/redwood/TrainTheme';

interface StepIconProps {
  /**
   * Determines if the step is disabled or not
   */
  isDisabled: boolean;
  /**
   * Determines if the step has been visited
   */
  isVisited: boolean;

  /**
   * A string that defines which icon type the step should take
   */
  messageType?: Exclude<MessageSeverity, 'none'>;
}

/**
 * Step icon
 */
export const StepIcon = ({ isDisabled, isVisited, messageType }: StepIconProps) => {
  const { index, selectedStepIndex } = useContext<TrainContextType>(Context);

  const isCurrent = selectedStepIndex === index;

  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle } = useInteractionStyle(
    isDisabled || isCurrent
  );

  const { isActive, activeProps } = useActive({ isDisabled: isDisabled || isCurrent });

  console.log('isActive', isActive);
  const mergedProps = mergeProps(interactionProps, activeProps);

  const { styles, variantClasses } = useComponentTheme<TrainVariantOptions, TrainStyles>(
    TrainRedwoodTheme,
    {
      stepIconDisabled: isDisabled ? 'isDisabled' : 'notDisabled',
      stepIconCurrent: isCurrent ? 'isCurrent' : 'notCurrent',
      stepIconVisited: isVisited ? 'isVisited' : 'notVisited',
      stepIconNeedsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
      stepIconPseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      stepIconActive: isActive ? 'isActive' : 'notActive'
    }
  );

  const iconStyles = () => {
    if (messageType) {
      return <UserChosenIcon messageType={messageType} isDisabled={isDisabled} />;
    } else {
      return (
        <span {...mergedProps} class={classNames([styles.stepIconBaseStyle, variantClasses])}>
          {index + 1}
        </span>
      );
    }
  };

  return <span class={styles.stepIconContainerStyle}>{iconStyles()}</span>;
};
